import { useCallback } from "react";
import { Link } from "react-router";
import { useWorkspace } from "../../app/workspace";
import { Button, ButtonLink } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { ErrorState } from "../../components/ui/ErrorState";
import { cx } from "../../components/ui/cx";
import { Empty, Loading, Meter, Note, PageHeader, Stat } from "../../components/ui/Page";
import { SrOnly, Table, Td, Th } from "../../components/ui/Table";
import { useNow } from "../../hooks/useNow";
import { usePoll } from "../../hooks/usePoll";
import { useResource } from "../../hooks/useResource";
import { metricsApi } from "../../lib/api/metrics";
import { streamsApi } from "../../lib/api/streams";
import type { Stream } from "../../lib/api/types";
import { formatBytes, formatHours, formatSpan, plural } from "../../lib/format";
import { exitReasonText, PLATFORM_LABEL, primaryAction, uptimeMs } from "../../lib/stream-status";
import { capacityHours } from "../../lib/uptime";
import { StatusPill } from "./StatusPill";
import { useStreamControl } from "./useStreamControl";

const POLL_MS = 5_000;

/** One square per stream the plan allows; filled while a stream holds the slot. */
function Slots({ used, limit }: { used: number; limit: number }) {
  return (
    <div aria-hidden="true" className="flex flex-wrap gap-1.5">
      {Array.from({ length: Math.max(limit, used) }, (_, i) => (
        <span key={i} className={cx("size-5 rounded-md border-2 border-ink motion-safe:animate-rise", i < used ? "bg-accent" : "bg-paper")}
          style={{ animationDelay: `${200 + i * 70}ms` }} />
      ))}
    </div>
  );
}

function Summary({ workspaceId, planName, live, limit, now }: { workspaceId: string; planName: string; live: number; limit: number; now: number }) {
  const m = useResource((signal) => metricsApi.summary(workspaceId, signal), [workspaceId]);
  const hours = m.data ? m.data.usage.month.secondsLive / 3600 : 0;
  const possible = m.data ? capacityHours(m.data.usage.month.from, now, limit) : 0;
  return (
    <section aria-label="Summary" className="grid grid-cols-[repeat(auto-fit,minmax(13.75rem,1fr))] gap-4">
      <Stat label="streams on" value={live} sub={`/ ${limit} allowed`}>
        <Slots used={live} limit={limit} />
      </Stat>
      <Stat label="stream hours this month" value={m.data ? formatHours(m.data.usage.month.secondsLive) : "-"}>
        {m.data && possible > 0 && <>
          <Meter value={Math.round(hours)} max={Math.round(possible)} label="stream hours used of the hours possible this month" />
          <span className="font-mono text-xs text-muted">of {Math.round(possible).toLocaleString()} h your plan could stream so far</span>
        </>}
      </Stat>
      {m.data && (
        <Stat label="storage" value={formatBytes(m.data.storage.bytes)} sub={`/ ${formatBytes(m.data.storage.limitBytes)}`}>
          <Meter value={m.data.storage.bytes} max={m.data.storage.limitBytes} label="storage used" />
          <Link to="/videos" className="font-mono text-xs text-muted">{plural(m.data.storage.files, "video")} →</Link>
        </Stat>
      )}
      <Stat label="plan" value={planName}>
        <Link to="/billing" className="font-mono text-xs text-muted">plans and billing →</Link>
      </Stat>
    </section>
  );
}

function StreamRow({ s, now, control, canControl }: {
  s: Stream; now: number; canControl: boolean; control: ReturnType<typeof useStreamControl>;
}) {
  const up = uptimeMs(s, now);
  const reason = s.actualState === "FAILED" || s.actualState === "RETRYING" ? exitReasonText(s.lastExitReason, s.platform) : null;
  const action = primaryAction(s);
  const n = s.playlist?.length ?? 0;
  return (
    <tr>
      <Td className="font-bold"><Link to={`/streams/${s.id}`}>{s.name}</Link></Td>
      <Td>
        <div className="flex flex-col items-start gap-1">
          <StatusPill stream={s} />
          {reason && <span className="max-w-xs text-xs text-muted">{reason}</span>}
          {control.errors[s.id] && <span role="alert" className="max-w-xs text-xs font-bold text-link-hover">{control.errors[s.id]}</span>}
        </div>
      </Td>
      <Td>{PLATFORM_LABEL[s.platform]}</Td>
      <Td className="text-muted">{plural(n, "video")} · {s.loopMode}</Td>
      <Td className="font-mono text-xs text-muted">{up === null ? "-" : formatSpan(up)}</Td>
      <Td className="text-right">
        {canControl && (
          <Button size="sm" variant={action === "start" ? "primary" : "secondary"} pending={control.busy === s.id}
            onClick={() => void control.toggle(s)} aria-label={`${action} ${s.name}`}>
            {action}
          </Button>
        )}
      </Td>
    </tr>
  );
}

export function StreamsPage() {
  const { workspace, can, writable } = useWorkspace();
  const list = useResource((signal) => streamsApi.list(workspace.id, signal), [workspace.id]);
  const now = useNow(30_000);
  usePoll(() => void list.reload(), POLL_MS, list.error === undefined);

  const onChanged = useCallback((s: Stream) => {
    list.data && list.set({ ...list.data, streams: list.data.streams.map((x) => (x.id === s.id ? { ...x, ...s } : x)) });
    void list.reload();
  }, [list]);
  const control = useStreamControl(onChanged);
  const canCreate = can("stream.write") && writable;

  return (
    <>
      <PageHeader
        caption={`workspace: ${workspace.name.toLowerCase()}`}
        title="Streams"
        actions={canCreate && <ButtonLink to="/streams/new" variant="primary">+ new stream</ButtonLink>}
      />
      <Summary
        workspaceId={workspace.id}
        planName={workspace.plan?.name ?? "none"}
        now={now}
        live={list.data?.usage.activeStreams ?? 0}
        limit={list.data?.usage.activeStreamLimit ?? workspace.plan?.maxConcurrentStreams ?? 0}
      />

      {list.error !== undefined && <ErrorState error={list.error} onRetry={() => void list.reload()} />}
      {!list.data && list.loading && <Loading />}
      {list.data && list.data.streams.length === 0 && (
        <Empty
          title="No streams yet"
          action={canCreate && <ButtonLink to="/streams/new" variant="primary">+ new stream</ButtonLink>}
        >
          A stream sends a playlist of your videos to YouTube, Twitch, Facebook or any RTMP server, looping as long as you like.
          Upload a video first under <Link to="/videos">videos</Link>.
        </Empty>
      )}
      {list.data && list.data.streams.length > 0 && (
        <Card raised>
          <Table label="Streams" minWidth={760}>
            <thead>
              <tr>
                <Th>name</Th><Th>status</Th><Th>platform</Th><Th>playlist</Th><Th>up for</Th><Th><SrOnly>actions</SrOnly></Th>
              </tr>
            </thead>
            <tbody>
              {list.data.streams.map((s) => (
                <StreamRow key={s.id} s={s} now={now} control={control} canControl={can("stream.control")} />
              ))}
            </tbody>
          </Table>
        </Card>
      )}
      <Note>status refreshes every 5 s. a FAILED stream shows its reason, e.g. a refused key: check it on the platform.</Note>
    </>
  );
}
